import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchChildIntelligence, generateChildInsight, fetchChildMilestones,
  listCopilotConversations,
} from "@/lib/api";
import {
  ArrowLeft, BookOpen, Trophy, Activity, Bell, Sparkles, Loader2,
  Mail, ClipboardCheck, CalendarClock, AlertTriangle, DollarSign, FileText,
  Stethoscope, GraduationCap, Music, Users, ChevronRight, Pin, Compass,
} from "lucide-react";
import { toast } from "sonner";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import ChildGrowthScores from "@/components/ChildGrowthScores";
import useTrackPage from "@/hooks/useTrackPage";

function ChildProfileInner() {
  const { childId } = useParams();
  useTrackPage("child_profile_open", { child_id: childId }, childId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [pinnedConvs, setPinnedConvs] = useState([]);

  useEffect(() => {
    setLoading(true);
    fetchChildIntelligence(childId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    fetchChildMilestones(childId)
      .then((r) => setMilestones(r.milestones || []))
      .catch(() => setMilestones([]));
    listCopilotConversations(childId)
      .then((r) => setPinnedConvs(r.conversations || []))
      .catch(() => setPinnedConvs([]));
  }, [childId]);

  const onGenerateInsight = async () => {
    setInsightLoading(true);
    try {
      const r = await generateChildInsight(childId);
      setInsight(r);
      if (!r.llm_available) toast.warning("AI insight unavailable right now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate insight.");
    } finally {
      setInsightLoading(false);
    }
  };

  const milestonesByMonth = useMemo(() => {
    const m = {};
    milestones.forEach((mil) => {
      const ym = (mil.occurred_at || "").slice(0, 7);
      if (!ym) return;
      (m[ym] ||= []).push(mil);
    });
    return Object.entries(m).sort((a, b) => b[0].localeCompare(a[0]));
  }, [milestones]);

  if (loading) {
    return (
      <div className="fcc-card p-16 text-center text-[var(--fcc-muted)]">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        <p className="text-sm mt-3">Loading child profile…</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="fcc-card p-12 text-center text-[var(--fcc-muted)]">
        <p>Couldn&apos;t load this child profile.</p>
        <Link to="/app/children" className="text-sm text-[var(--fcc-green)] font-semibold mt-3 inline-block">← Back to all children</Link>
      </div>
    );
  }

  const child = data.child;
  const sch = data.school;
  const act = data.activities;
  const att = data.parent_attention;
  const wins = data.recent_wins || [];
  const totals = data.totals;

  return (
    <div data-testid="child-profile-page" className="space-y-8">
      <Link to="/app/children" data-testid="child-profile-back" className="inline-flex items-center gap-1.5 text-sm text-[var(--fcc-ink-soft)] hover:text-[var(--fcc-green)]">
        <ArrowLeft className="w-4 h-4" /> All children
      </Link>

      <header className="fcc-card p-7 sm:p-8" data-testid="child-profile-header">
        <p className="label-overline">Child profile</p>
        <div className="flex items-center justify-between flex-wrap gap-4 mt-2">
          <div>
            <h1 className="font-display text-4xl text-[var(--fcc-ink)]">{child.name}</h1>
            <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">
              {child.grade || "Grade —"}{child.school ? ` · ${child.school}` : ""}
              {child.birthdate ? ` · born ${child.birthdate}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Link
              to={`/app/children/${childId}/journey`}
              data-testid="child-profile-journey-link"
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[var(--fcc-border)] bg-white text-sm font-semibold text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] transition"
            >
              <Compass className="w-4 h-4" /> View journey
            </Link>
            <button
              onClick={onGenerateInsight}
              disabled={insightLoading}
              data-testid="child-profile-generate-insight"
              className="fcc-btn-primary text-sm py-2.5 px-4 inline-flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> {insightLoading ? "Generating…" : "Generate AI insight"}
            </button>
          </div>
        </div>
        <ul className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <BigStat label="Child emails (30d)" value={totals.child_emails_this_month} Icon={Mail} />
          <BigStat label="Open tasks" value={totals.open_tasks} Icon={ClipboardCheck} />
          <BigStat label="Unconfirmed events" value={totals.unconfirmed_events} Icon={CalendarClock} />
          <BigStat label="Lifetime milestones" value={totals.milestones_lifetime} Icon={Trophy} />
        </ul>
      </header>

      {insight && insight.narrative && (
        <section className="fcc-card-accent p-6 sm:p-7" data-testid="child-profile-insight">
          <p className="label-overline flex items-center gap-2"><Sparkles className="w-3 h-3" /> AI insight · last 30 days</p>
          <p className="text-[var(--fcc-ink)] mt-3 leading-relaxed">{insight.narrative}</p>
          <p className="text-xs text-[var(--fcc-muted)] mt-3">Generated {new Date(insight.generated_at).toLocaleString()}</p>
        </section>
      )}

      <ChildGrowthScores childId={childId} />

      {pinnedConvs.length > 0 && (
        <section data-testid="pinned-conversations" className="fcc-card p-6 sm:p-7">
          <header className="flex items-baseline justify-between mb-4">
            <div>
              <p className="label-overline flex items-center gap-2"><Pin className="w-3 h-3" /> Pinned Copilot conversations</p>
              <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">Decisions & plans you saved</h2>
            </div>
          </header>
          <ul className="space-y-2.5">
            {pinnedConvs.map((c) => (
              <li key={c.id} data-testid="pinned-conversation-row" className="rounded-xl border border-[var(--fcc-border)] p-3.5 bg-[var(--fcc-cream)]/40">
                <p className="font-semibold text-sm text-[var(--fcc-ink)]">{c.title}</p>
                {c.summary && <p className="text-xs text-[var(--fcc-ink-soft)] mt-1">{c.summary}</p>}
                <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mt-2">
                  {(c.created_at || "").slice(0, 10)}
                  {c.pinned ? " · pinned" : ""}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Bucket icon={BookOpen} title="School" testid="child-bucket-school" tint="bg-[var(--fcc-emma)]">
          <CountRow Icon={Mail} label="Emails this month" value={sch.emails_this_month} />
          <CountRow Icon={GraduationCap} label="Teacher communications" value={sch.teacher_communications} />
          <CountRow Icon={Bell} label="Attendance notices" value={sch.attendance_notices} />
          <ListSection label="Assignments due" items={sch.assignments_due} testid="child-school-assignments">
            {(t) => (
              <>
                <span className="font-semibold text-sm text-[var(--fcc-ink)]">{t.title}</span>
                {t.due_date && <span className="text-xs text-[var(--fcc-muted)] ml-auto">{t.due_date.slice(0, 10)}</span>}
              </>
            )}
          </ListSection>
          <ListSection label="Recent" items={sch.recent} testid="child-school-recent">
            {(e) => <RecentRow e={e} />}
          </ListSection>
        </Bucket>

        <Bucket icon={Activity} title="Activities" testid="child-bucket-activities" tint="bg-[var(--fcc-green-soft)]">
          <ActivityBlock
            Icon={Trophy}
            label="Sports"
            count={act.sports.count}
            recent={act.sports.recent}
          />
          <ActivityBlock
            Icon={Music}
            label="Clubs / camps / music"
            count={act.clubs_and_activities.count}
            recent={act.clubs_and_activities.recent}
          />
          <ActivityBlock
            Icon={Stethoscope}
            label="Medical"
            count={act.medical.count}
            recent={act.medical.recent}
          />
        </Bucket>

        <Bucket icon={AlertTriangle} title="Parent attention" testid="child-bucket-attention" tint="bg-[var(--fcc-red-soft)]">
          <ListSection label="Forms due" items={att.forms_due} testid="child-attention-forms" Icon={FileText}>
            {(t) => <DeadlineRow t={t} />}
          </ListSection>
          <ListSection label="Payments due" items={att.payments_due} testid="child-attention-payments" Icon={DollarSign}>
            {(t) => <DeadlineRow t={t} />}
          </ListSection>
          <ListSection label="Overdue" items={att.overdue} testid="child-attention-overdue" Icon={AlertTriangle}>
            {(t) => <DeadlineRow t={t} overdue />}
          </ListSection>
          <ListSection label="Upcoming deadlines" items={att.upcoming_deadlines} testid="child-attention-upcoming" Icon={CalendarClock}>
            {(t) => <DeadlineRow t={t} />}
          </ListSection>
          <ListSection label="Unconfirmed events" items={att.calendar_suggestions} testid="child-attention-events" Icon={CalendarClock}>
            {(e) => (
              <>
                <span className="font-semibold text-sm text-[var(--fcc-ink)] truncate">{e.title}</span>
                <span className="text-xs text-[var(--fcc-muted)] ml-auto">{(e.event_date || "").slice(0, 10)}</span>
              </>
            )}
          </ListSection>
        </Bucket>

        <Bucket icon={Trophy} title="Recent wins" testid="child-bucket-wins" tint="bg-[var(--fcc-noah)]">
          {wins.length === 0 ? (
            <p className="text-sm text-[var(--fcc-muted)]" data-testid="child-wins-empty">
              No milestones detected yet. KidHQ surfaces report cards, honor roll, championships, and other proud moments automatically.
            </p>
          ) : (
            <ul className="space-y-2.5">
              {wins.map((w, idx) => (
                <li key={idx} data-testid="child-win-row" className="rounded-xl border border-[var(--fcc-border)] p-3 flex items-start gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[var(--fcc-cream-2)] flex items-center justify-center shrink-0">
                    <Trophy className="w-3.5 h-3.5 text-[var(--fcc-green)]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[var(--fcc-ink)]">{w.label}</p>
                    <p className="text-xs text-[var(--fcc-muted)] mt-0.5">
                      {(w.occurred_at || "").slice(0, 10)}
                      {w.kind ? ` · ${w.kind}` : ""}
                      {typeof w.confidence === "number" ? ` · confidence ${(w.confidence * 100).toFixed(0)}%` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Bucket>
      </div>

      <section data-testid="child-milestones-timeline">
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-4">All milestones · timeline</h2>
        {milestonesByMonth.length === 0 ? (
          <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]">
            No milestones yet for {child.name}. They&apos;ll appear here automatically as KidHQ detects them in your emails.
          </div>
        ) : (
          <div className="space-y-5">
            {milestonesByMonth.map(([ym, items]) => (
              <article key={ym} className="fcc-card p-5" data-testid={`milestone-month-${ym}`}>
                <header className="flex items-baseline justify-between">
                  <h3 className="font-display text-xl text-[var(--fcc-ink)]">{formatMonth(ym)}</h3>
                  <span className="text-xs text-[var(--fcc-muted)]">{items.length}</span>
                </header>
                <ol className="mt-3 relative border-l-2 border-[var(--fcc-border)] pl-5 space-y-3">
                  {items.map((m) => (
                    <li key={m.id} data-testid="milestone-row" className="relative">
                      <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full border-2 border-white bg-[var(--fcc-green)]" />
                      <p className="font-semibold text-sm text-[var(--fcc-ink)]">{m.label}</p>
                      <p className="text-xs text-[var(--fcc-muted)] mt-0.5">
                        {(m.occurred_at || "").slice(0, 10)}
                        {m.kind ? ` · ${m.kind}` : ""}
                        {m.matched_phrase ? ` · "${m.matched_phrase}"` : ""}
                      </p>
                    </li>
                  ))}
                </ol>
              </article>
            ))}
          </div>
        )}
      </section>

      <Link
        to={`/app/copilot?prefill=${encodeURIComponent(`Tell me about ${child.name} this week`)}`}
        data-testid="child-profile-copilot-cta"
        className="fcc-card p-5 flex items-center gap-4 hover:bg-[var(--fcc-cream-2)] transition"
      >
        <span className="w-11 h-11 rounded-2xl bg-[var(--fcc-emma)] flex items-center justify-center shrink-0">
          <Users className="w-4 h-4 text-[var(--fcc-green)]" />
        </span>
        <div className="flex-1">
          <p className="font-display text-lg text-[var(--fcc-ink)]">Ask the Copilot about {child.name}</p>
          <p className="text-xs text-[var(--fcc-ink-soft)] mt-0.5">Conversational answers grounded in this child&apos;s real data.</p>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--fcc-muted)]" />
      </Link>
    </div>
  );
}

function Bucket({ icon: Icon, title, testid, tint, children }) {
  return (
    <section data-testid={testid} className="fcc-card p-6 space-y-3">
      <div className="flex items-center gap-3">
        <span className={`inline-flex w-10 h-10 rounded-xl items-center justify-center ${tint}`}>
          <Icon className="w-4 h-4 text-[var(--fcc-green)]" />
        </span>
        <h3 className="font-display text-xl text-[var(--fcc-ink)]">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function BigStat({ label, value, Icon }) {
  return (
    <li className="rounded-xl border border-[var(--fcc-border)] p-3.5 bg-white">
      <span className="inline-flex w-8 h-8 rounded-lg items-center justify-center bg-[var(--fcc-green-soft)]">
        <Icon className="w-3.5 h-3.5 text-[var(--fcc-green)]" />
      </span>
      <p className="text-2xl font-display text-[var(--fcc-ink)] mt-2.5 leading-none">{value ?? 0}</p>
      <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mt-1.5">{label}</p>
    </li>
  );
}

function CountRow({ Icon, label, value }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-white border border-[var(--fcc-border)] px-3 py-2 text-sm">
      <span className="inline-flex items-center gap-2 text-[var(--fcc-ink-soft)]">
        <Icon className="w-3.5 h-3.5 text-[var(--fcc-muted)]" />
        {label}
      </span>
      <span className="font-semibold text-[var(--fcc-ink)]">{value ?? 0}</span>
    </div>
  );
}

function ListSection({ label, items, testid, Icon, children }) {
  const rows = items || [];
  if (rows.length === 0) return null;
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mb-1.5 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}{label} · {rows.length}
      </p>
      <ul className="space-y-1.5">
        {rows.slice(0, 5).map((it, idx) => (
          <li key={idx} className="flex items-center gap-2 rounded-lg bg-white border border-[var(--fcc-border)] px-3 py-2">
            {children(it)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActivityBlock({ Icon, label, count, recent }) {
  return (
    <div className="rounded-xl border border-[var(--fcc-border)] p-3.5 bg-white">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--fcc-ink)]">
          <Icon className="w-3.5 h-3.5 text-[var(--fcc-green)]" /> {label}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{count ?? 0} emails</span>
      </div>
      {(recent || []).length > 0 && (
        <ul className="mt-2 space-y-1">
          {recent.map((r, idx) => (
            <li key={idx} className="text-xs text-[var(--fcc-ink-soft)] truncate">
              · {r.subject || r.summary || "(no subject)"}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DeadlineRow({ t, overdue }) {
  return (
    <>
      <span className="font-semibold text-sm text-[var(--fcc-ink)] truncate">{t.title}</span>
      {t.priority && (
        <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] ml-1.5">{t.priority}</span>
      )}
      {t.due_date && (
        <span className={`text-xs ml-auto ${overdue ? "text-rose-700" : "text-[var(--fcc-muted)]"}`}>
          {t.due_date.slice(0, 10)}
        </span>
      )}
    </>
  );
}

function RecentRow({ e }) {
  return (
    <>
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-sm text-[var(--fcc-ink)] truncate block">{e.subject}</span>
        {e.summary && <span className="text-xs text-[var(--fcc-ink-soft)] truncate block">{e.summary}</span>}
      </div>
      <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] shrink-0 ml-2">{(e.received_at || "").slice(0, 10)}</span>
    </>
  );
}

function formatMonth(ym) {
  if (!ym) return "Earlier";
  const [y, m] = ym.split("-");
  const dt = new Date(Number(y), Number(m) - 1, 1);
  if (Number.isNaN(dt.getTime())) return ym;
  return dt.toLocaleString("en-US", { month: "long", year: "numeric" });
}

export default function ChildProfilePage() {
  return (
    <PageErrorBoundary scope="child-profile">
      <ChildProfileInner />
    </PageErrorBoundary>
  );
}
